import type { Experiment } from 'types';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DataTable, useDataControl } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useTestExperiments } from '@/state/experiments/useTestExperiments';
import { Copy } from 'lucide-react';
import { EmptyState, Panel, PillTabs } from '../../Dashboard/ConsolePanel';
import { getTestExperimentColumns } from './columns';
import { DuplicateExperimentModal } from './DuplicateExperimentModal';

export function TestExperiments() {
  const {
    testExperiments,
    isLoading,
    VIEWS,
    view,
    setView,
    counts,
    duplicateExperiment,
    deleteTestExperiment,
    resetTestExperiment,
  } = useTestExperiments();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [wantsToDelete, setWantsToDelete] = useState<Experiment | null>(null);
  const [wantsToReset, setWantsToReset] = useState<Experiment | null>(null);

  const dataController = useDataControl<Experiment>(testExperiments);

  const tabList = VIEWS.map((v) => ({
    value: v,
    label: `${v} (${counts[v]})`,
  }));

  const columns = getTestExperimentColumns({
    onDelete: setWantsToDelete,
    onReset: setWantsToReset,
  });

  const onDeleteConfirm = async () => {
    if (!wantsToDelete) return;
    await deleteTestExperiment(wantsToDelete.id);
    setWantsToDelete(null);
  };

  const onResetConfirm = async () => {
    if (!wantsToReset) return;
    await resetTestExperiment(wantsToReset.id);
    setWantsToReset(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-[14px]">
        <Panel title="Test experiments">
          <EmptyState>Loading test experiments...</EmptyState>
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[14px]">
      <Panel
        title="Test experiments"
        actions={
          <>
            <PillTabs options={tabList} value={view} onChange={(v) => setView(v as typeof view)} />
            <Button onClick={() => setShowDuplicateModal(true)} className="h-9 rounded-lg">
              <Copy className="mr-2 h-4 w-4" />
              Duplicate Experiment
            </Button>
          </>
        }
        bodyClassName="p-3"
      >
        <DataTable<Experiment> dataController={dataController} pageSize={10} columns={columns} />
      </Panel>

      <DuplicateExperimentModal
        open={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onDuplicate={duplicateExperiment}
      />

      <ConfirmDialog
        title={`Delete test experiment "${wantsToDelete?.name}"?`}
        open={!!wantsToDelete}
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={onDeleteConfirm}
        onCancel={() => setWantsToDelete(null)}
      >
        This will permanently delete the experiment, its variants, changes, and all related stats. This cannot be
        undone.
      </ConfirmDialog>

      <ConfirmDialog
        title={`Reset test experiment "${wantsToReset?.name}"?`}
        open={!!wantsToReset}
        confirmLabel="Reset"
        confirmVariant="destructive"
        onConfirm={onResetConfirm}
        onCancel={() => setWantsToReset(null)}
      >
        This will move the experiment back to Draft, clear its start/end timestamps, and delete all its stats. Variants
        and changes are preserved.
      </ConfirmDialog>
    </div>
  );
}
